import { Link } from "react-router-dom";
import { ArrowRight, Target, HeartHandshake, Trophy, Compass, MessageCircle } from "lucide-react";
import SectionTitle from "@/components/SectionTitle";
import { BRAND, PHONES, buildWhatsAppLink } from "@/lib/constants";

const values = [
    { Icon: Target, title: "Propósito", text: "Facilitar a rotina de pessoas e empresas com soluções inteligentes e confiáveis." },
    { Icon: HeartHandshake, title: "Atendimento Humano", text: "Acreditamos no contato próximo, respeitoso e ágil em cada interação com o cliente." },
    { Icon: Trophy, title: "Excelência", text: "Equipe treinada, processos definidos e tecnologia para entregar o melhor resultado." },
    { Icon: Compass, title: "Clareza", text: "Transparência total em preços, prazos e condições. Sem letrinhas miúdas." },
];

export default function Sobre() {
    return (
        <div data-testid="sobre-page">
            {/* Hero */}
            <section className="bg-[#1a1a1a] text-white py-20 md:py-28 relative overflow-hidden">
                <div className="absolute -bottom-40 -right-20 w-[500px] h-[500px] bg-[#fec200] opacity-15 blur-[120px] rounded-full" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#fec200] mb-4 border-l-4 border-[#fec200] pl-3">
                                Sobre a PEG Serviços
                            </p>
                            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                                Movendo pessoas.<br />
                                Criando <span className="text-[#fec200]">caminhos</span>.
                            </h1>
                            <p className="mt-6 text-lg text-white/70 leading-relaxed">
                                Onde a clareza guia e a atitude transforma. Conheça a história de quem trabalha todos os dias para oferecer mobilidade, segurança e tranquilidade aos clientes em toda a Bahia.
                            </p>
                        </div>
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#fec200] translate-x-3 translate-y-3" />
                            <div className="relative bg-[#fec200]">
                                <img
                                    src={BRAND.mascot}
                                    alt="Mascote PEG"
                                    className="w-full h-auto object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* História */}
            <section className="bg-[#f5f5f5] py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-7">
                            <SectionTitle
                                eyebrow="Nossa História"
                                title="Uma empresa nascida para resolver."
                            />
                            <div className="mt-8 space-y-5 text-[#1a1a1a] leading-relaxed">
                                <p>
                                    A <strong>PEG Serviços</strong> nasceu com o propósito de oferecer soluções que facilitam a rotina de pessoas e empresas. Começamos pequeno, com a vontade de fazer diferente em um mercado que muitas vezes parecia distante do cliente.
                                </p>
                                <p>
                                    Hoje, atuamos em três frentes complementares: <strong>aluguel de veículos</strong>, <strong>rastreamento veicular</strong> e <strong>guincho 24 horas</strong>. Tudo pensado para que o cliente tenha um único parceiro confiável em todos os momentos da sua relação com o veículo.
                                </p>
                                <p>
                                    Com atendimento humanizado, agilidade e compromisso, trabalhamos para garantir mobilidade, segurança e tranquilidade. Nossa equipe está preparada para atender com excelência, oferecendo serviços modernos e eficientes que geram resultados reais.
                                </p>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="bg-[#1a1a1a] text-white p-8 md:p-10 sticky top-28">
                                <p className="font-display font-black text-6xl text-[#fec200] leading-none">
                                    "
                                </p>
                                <p className="font-display text-2xl font-bold leading-snug mt-2">
                                    Onde a clareza guia e a atitude transforma.
                                </p>
                                <p className="mt-6 text-sm text-white/60 uppercase tracking-widest font-semibold">
                                    — Nossa filosofia
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Valores */}
            <section className="bg-white py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionTitle
                        eyebrow="Valores"
                        title="O que nos move todos os dias"
                        subtitle="São os princípios que orientam cada decisão e cada atendimento da PEG Serviços."
                    />
                    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {values.map(({ Icon, title, text }) => (
                            <div
                                key={title}
                                data-testid={`valor-${title.toLowerCase()}`}
                                className="bg-[#f5f5f5] p-7 border-b-4 border-[#fec200] hover:bg-[#1a1a1a] hover:text-white transition-colors group"
                            >
                                <Icon className="w-10 h-10 text-[#fec200]" />
                                <h3 className="font-display text-2xl font-black mt-5">{title}</h3>
                                <p className="mt-3 text-[#4a4a4a] group-hover:text-white/70 text-sm leading-relaxed">
                                    {text}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#fec200] py-16">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
                    <h3 className="font-display text-2xl md:text-3xl font-black text-[#1a1a1a]">
                        Pronto para experimentar o atendimento PEG?
                    </h3>
                    <a
                        href={buildWhatsAppLink(PHONES.aluguel.raw, "Olá PEG! Quero conhecer melhor a empresa.")}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="sobre-cta-whatsapp"
                        className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white font-bold px-8 py-4 hover:bg-white hover:text-[#1a1a1a] transition-colors"
                    >
                        <MessageCircle className="w-5 h-5" />
                        Falar com a PEG
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </section>
        </div>
    );
}
